/**
 * UserStampHistoryContainer
 * --
 */
import React, { useState, useEffect, useMemo } from 'react';
import MessageAlert from 'utils';
import UserStampHistoryPresenter from './UserStampHistoryPresenter';
import dayjs from 'dayjs';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetUsers } from 'hooks/UserHooks';
import {
  useBulkUpdateStampHistory,
  useGetStampHistorys,
  useRewardStampHistory,
} from 'hooks/StampHistoryHooks';
import { useGetSetting } from 'hooks/SettingHooks';
/**----------------------------------------
 * [Component] 총관리자: 회원관리 -> 스탬프내역
 * ----------------------------------------
 */
const INITIAL_VALUE = {
  type: '',
  isReward: '',
  start_date: dayjs().subtract(1, 'months').startOf('day'),
  end_date: dayjs().endOf('day'),
  value: 30,
  reward_stamp: 0,
  expired_date: 0,
  update_date: 0,
};
const UserStampHistoryContainer = ({ permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [period, setPeriod] = useState({
    start_date: dayjs().subtract(1, 'months').startOf('day').unix(),
    end_date: dayjs().endOf('day').unix(),
  });
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [selectedList, setSelectedList] = useState([]);

  const confirm = (msg = '') => window.confirm(msg);

  /* ===== MUTATE ===== */
  const { data: StampSetting } = useGetSetting(1, null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data.stamp_setting;
    },
    initialData: null,
  });

  const { data: ShopInformationList } = useGetShopInformations(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  const { data: UserList } = useGetUsers(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });
  const { data: StampHistoryList, refetch: refetchStampHistoryList } =
    useGetStampHistorys(
      { start_date: period.start_date, end_date: period.end_date },
      {
        select: (res) => {
          const { status, data } = res;

          if (status !== 200) {
            return [];
          }

          return data;
        },
        initialData: [],
      }
    );
  const { rewardStampHistory } = useRewardStampHistory();
  const { bulkUpdateStampHistory } = useBulkUpdateStampHistory();

  const newUserList = useMemo(() => {
    const userList = UserList?.map((user) => {
      const userStampHistory = StampHistoryList?.filter(
        (stamp) => stamp?.user_id === user?.user_id
      ).map((stamp) => {
        const matchedShop = ShopInformationList?.find(
          (shop) => stamp?.shop_information_id === shop?.shop_information_id
        );
        return {
          key: stamp?.stamp_history_id,
          ...stamp,
          shop_information_name: matchedShop?.shop_information_name || '-',
          reward_type:
            !stamp?.order_id | !stamp?.shop_information_id
              ? '관리자 지급'
              : '주문완료 적립',
        };
      });
      return {
        key: user?.user_id,
        ...user,
        user_type: user?.user_name ? 'MEMBER' : 'NONE',
        user_name: user?.user_name || '비회원',
        stamp_history: userStampHistory,
      };
    });
    return userList;
  }, [UserList, ShopInformationList, StampHistoryList]);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };
  const refreshData = () => {
    refetchStampHistoryList();
  };

  // /**--------------------
  //  * 스탬프 지급/만료 처리
  //  * --------------------
  //  * @param {object | array} data
  //  * @param {number} reserve
  //  */
  const handleStampUpdate = async (data, v, t, callback) => {
    try {
      let res;
      //적립
      if (t === 'REWARD') {
        if (!data) return MessageAlert.error('지급 대상을 선택해주세요.');
        if (!v.reward_stamp) {
          MessageAlert.error('지급할 스탬프를 입력해주세요.');
          return;
        }
        if (!v.expired_date) {
          if (
            !confirm(
              `만료일이 없을시 관리자에서 설정한 만료일 만큼 설정됩니다.\n관리자 설정 : 지급일 기준 ${StampSetting?.expired_date}일 뒤 만료`
            )
          )
            return;
        }
        if (
          !confirm(
            `${data?.user_name}님께 스탬프 ${v.reward_stamp}개를 지급 하시겠습니까?`
          )
        )
          return;
        res = await rewardStampHistory({
          user_id: data.user_id,
          new_stamp_count: v.reward_stamp,
          expired_date: v.expired_date || StampSetting?.expired_date,
          goal_num: StampSetting?.goal_num,
          coupon_id: StampSetting?.coupon_id,
        });
      }
      //만료
      if (t === 'EXPRIED') {
        const filterData = selectedList?.filter(
          (item) => !item?.use_date && item.expired_date >= dayjs().unix()
        );
        if (
          !confirm(
            `이미 사용 및 만료 처리된 건 제외한 ${filterData?.length}건을 만료처리하겠습니까?`
          )
        )
          return;
        res = await bulkUpdateStampHistory(
          filterData?.map((item) => ({
            ...item,
            expired_date: inputValue.update_date.unix(),
          }))
        );
      }

      if (res.status !== 200) {
        MessageAlert.error(
          `스탬프 ${t === 'REWARD' ? '지급' : '만료처리'}에 실패하였습니다!`
        );
        return;
      }
      refreshData();
      MessageAlert.success(
        `스탬프가 ${t === 'REWARD' ? '지급' : '만료처리'} 되었습니다.`
      );
    } catch (err) {
      console.error('[handleRewardStamp] Error!! ', err);
      MessageAlert.error(
        `스탬프 ${t === 'REWARD' ? '지급' : '만료처리'}에 실패하였습니다!`
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  /**
   * 회원 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (type, obj) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        setSelectedList((prev) => [...prev, obj]);
        return;
      case 'UNCHECKED': // 개별 선택 해제
        setSelectedList((prev) =>
          prev.filter((select) => select.key !== obj.key)
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        setSelectedList(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = selectedList.filter((user) =>
          obj.some((ob) => ob.key === user.key)
        );

        setSelectedList(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = selectedList.length === obj.length;

        if (!isAll) {
          setSelectedList(obj);
        } else {
          setSelectedList([]);
        }
        return;

      case 'RESET':
        setSelectedList([]);
        return;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  // /* 초기 설정 & 업체변경 시 반영 */
  // useEffect(() => {
  //   currentShop && handleGetReviewList();
  // }, [currentShop]);

  /* ===== RENDER ===== */
  return (
    <UserStampHistoryPresenter
      {...{
        /* States */
        userList: newUserList,
        permission,
        loading,
        currentUser,
        setCurrentUser,
        period,
        setPeriod,
        StampHistoryList,
        onChange: handleChange,
        inputValue: inputValue,
        setInputValue: setInputValue,
        onStampUpdate: handleStampUpdate,
        /* Events */
        setLoading,
        onSelect: handleSelect,
        selectedList,
        INITIAL_VALUE,
        StampSetting,
      }}
    />
  );
};

/* DF */
UserStampHistoryContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
};
export default UserStampHistoryContainer;
